import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

interface Session {
  id: string;
  contact_id: string;
  email: string;
  first_name: string | null;
  message_count: number;
  created_at: string;
  last_active_at: string;
  ontraport_note_id: string | null;
  tag_applied: boolean | null;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  grounds: any;
  cta: string | null;
  created_at: string;
}

const GROUND_LABEL: Record<string, string> = {
  settled_law: "Settled law",
  documented_position: "Our documented position",
  live_conversation: "Belongs in a live conversation",
};
const CTA_LABEL: Record<string, string> = {
  book_call: "Book your call",
  access_fddr: "Request access to the full report",
};

const WIDGET_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://wlthystwrd.com";

const EMBED_SNIPPET = `<script src="${WIDGET_ORIGIN}/stew-launcher.js" data-contact-id="ONTRAPORT_CONTACT_ID_MERGE_FIELD" data-email="ONTRAPORT_EMAIL_MERGE_FIELD" defer></script>`;

export default function ProspectConversations() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [copied, setCopied] = useState<"embed" | "contact" | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("public_stew_sessions")
        .select("id, contact_id, email, first_name, message_count, created_at, last_active_at, ontraport_note_id, tag_applied")
        .order("last_active_at", { ascending: false })
        .limit(200);
      if (error) {
        toast.error("Could not load sessions");
      } else {
        setSessions((data || []) as Session[]);
      }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      (s) =>
        s.email.toLowerCase().includes(q) ||
        s.contact_id.toLowerCase().includes(q),
    );
  }, [sessions, query]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    (async () => {
      setMsgLoading(true);
      const { data, error } = await supabase
        .from("public_stew_messages")
        .select("id, role, content, grounds, cta, created_at")
        .eq("session_id", selectedId)
        .order("created_at", { ascending: true });
      if (error) toast.error("Could not load transcript");
      else setMessages((data || []) as Message[]);
      setMsgLoading(false);
    })();
  }, [selectedId]);

  const selected = sessions.find((s) => s.id === selectedId) || null;

  async function copy(text: string, kind: "embed" | "contact") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Could not copy");
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      <div>
        <h1 className="text-2xl font-semibold">Prospect Conversations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Public Stew sessions started from the briefing widget.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Embed on your Ontraport briefing page</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <pre className="text-xs bg-muted rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all">
            {EMBED_SNIPPET}
          </pre>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => copy(EMBED_SNIPPET, "embed")}>
              {copied === "embed" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              <span className="ml-1.5">{copied === "embed" ? "Copied" : "Copy snippet"}</span>
            </Button>
            <p className="text-xs text-muted-foreground">
              Paste this just before the closing body tag of the briefing page, then replace the two placeholders with your Ontraport merge fields for contact id and email. A small green chat bubble will appear in the bottom right corner.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        <Card className="lg:h-[70vh] flex flex-col">
          <CardHeader className="pb-2 space-y-2">
            <CardTitle className="text-base">Sessions</CardTitle>
            <Input
              placeholder="Search email or contact id"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto pt-0">
            {loading ? (
              <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">No sessions yet.</p>
            ) : (
              <ul className="space-y-1">
                {filtered.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      className={`w-full text-left rounded-md border p-2.5 text-sm transition-colors ${
                        selectedId === s.id ? "bg-muted border-primary/40" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="font-medium truncate">
                        {s.first_name ? <span>{s.first_name} · </span> : null}
                        {s.email}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">
                        {s.contact_id}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span>{s.message_count} msgs</span>
                        <span>·</span>
                        <span>started {new Date(s.created_at).toLocaleDateString()}</span>
                        <span>·</span>
                        <span>active {new Date(s.last_active_at).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-1 flex gap-1">
                        {s.ontraport_note_id && (
                          <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Note</Badge>
                        )}
                        {s.tag_applied && (
                          <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Tagged</Badge>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="lg:h-[70vh] flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-base truncate">
                  {selected ? (selected.first_name ? `${selected.first_name} · ${selected.email}` : selected.email) : "Select a session"}
                </CardTitle>
                {selected && (
                  <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-1.5 mt-0.5">
                    <span className="truncate">{selected.contact_id}</span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => copy(selected.contact_id, "contact")}
                      aria-label="Copy contact id"
                    >
                      {copied === "contact" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto pt-0">
            {!selected ? (
              <p className="text-sm text-muted-foreground">Pick a session on the left to view its transcript.</p>
            ) : msgLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading
              </div>
            ) : messages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No messages in this session.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((m) => (
                  <TranscriptRow key={m.id} m={m} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TranscriptRow({ m }: { m: Message }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl bg-primary text-primary-foreground px-3.5 py-2 text-sm whitespace-pre-wrap">
          {m.content}
        </div>
      </div>
    );
  }
  const grounds = Array.isArray(m.grounds) ? m.grounds : [];
  const groundLabel = grounds.find((g: any) => g?.flag === "crisis")
    ? "Crisis flag"
    : null;
  const ctaLabel = m.cta && m.cta !== "none" ? CTA_LABEL[m.cta] || null : null;
  return (
    <div className="flex flex-col gap-1">
      <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-2">
        <span>{new Date(m.created_at).toLocaleString()}</span>
        {groundLabel && <span>· {groundLabel}</span>}
        {ctaLabel && <span>· CTA: {ctaLabel}</span>}
        {grounds.length > 0 && !groundLabel && (
          <span>
            ·{" "}
            {grounds
              .map((g: any) => g?.title || g?.section_key)
              .filter(Boolean)
              .slice(0, 3)
              .join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}
